import { ChevronDown, LayoutGrid, LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { DropdownMenu, type DropdownItem } from '@/components/ui';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function UserMenu({ showSettings = true }: { showSettings?: boolean }) {
  const user = useCurrentUser();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const items: DropdownItem[] = [
    { id: 'categories', label: 'Switch business category', icon: <LayoutGrid size={16} />, onSelect: () => navigate('/categories') },
    ...(showSettings ? [{ id: 'settings', label: 'Settings', icon: <Settings size={16} />, onSelect: () => navigate('/settings') }] : []),
    {
      id: 'logout',
      label: 'Sign out',
      icon: <LogOut size={16} />,
      separatorBefore: true,
      onSelect: () => {
        void signOut().finally(() => navigate('/login', { replace: true }));
      },
    },
  ];

  return (
    <DropdownMenu
      label="User menu"
      items={items}
      minWidth={240}
      header={
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{user.displayName}</p>
          <p className="truncate text-xs text-ink-secondary">{user.email}</p>
          <p className="mt-1 text-[11px] text-ink-muted">{user.role.name}</p>
        </div>
      }
      renderTrigger={(props) => (
        <button
          {...props}
          type="button"
          aria-label={`User menu for ${user.displayName}`}
          className="flex items-center gap-2.5 rounded-md py-1 pl-1 pr-2 transition-colors hover:bg-surface-sunken focus-visible:shadow-focus focus-visible:outline-none"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
            {initials(user.displayName)}
          </span>
          <span className="hidden text-left leading-tight md:block">
            <span className="block text-[13px] font-medium text-ink">{user.displayName}</span>
            <span className="block text-[11px] text-ink-muted">{user.email}</span>
          </span>
          <ChevronDown size={16} aria-hidden="true" className="text-ink-muted" />
        </button>
      )}
    />
  );
}
